import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for copying text to clipboard with proper cleanup.
 * Handles both modern clipboard API and legacy fallback.
 * Automatically clears the "copied" state after a timeout.
 *
 * @param timeoutMs - Duration to show "copied" feedback (default: 2000ms)
 * @returns { copied, copy } - copied state and copy function
 */
export const useCopyToClipboard = (timeoutMs = 2000) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const copy = useCallback(async (text: string): Promise<boolean> => {
    if (!text) return false;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const scheduleReset = () => {
      timerRef.current = setTimeout(() => setCopied(false), timeoutMs);
    };

    try {
      // Modern clipboard API
      await navigator.clipboard.writeText(text);
      setCopied(true);
      scheduleReset();
      return true;
    } catch {
      // Fallback for older browsers or when clipboard API is not available
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        scheduleReset();
        return true;
      } catch {
        return false;
      }
    }
  }, [timeoutMs]);

  return { copied, copy };
};

export default useCopyToClipboard;
